/**
 * Pure helpers for the chat route.
 *
 * Extracted from src/app/(app)/api/chat/route.ts so the route file only
 * exports Next.js-valid things (POST + route segment config). Next.js
 * build fails with "X is not a valid Route export field" if a route
 * file exports arbitrary symbols.
 */

const PRODUCT_STOPWORDS = new Set([
  'was', 'wie', 'wo', 'ihr', 'habt', 'ich', 'der', 'die', 'das', 'und',
  'the', 'you', 'are', 'do', 'does', 'is', 'of', 'a', 'to',
])

// Common non-product query words (lowercase, accent-stripped below). Filters
// out legal/hours/contact/shipping/payment questions so we don't hit the DB
// for them. Compound German nouns like "Öffnungszeiten" are long enough to
// survive the stopword filter alone, so we deny them explicitly.
const NON_PRODUCT_KEYWORDS = new Set([
  'öffnungszeiten', 'oeffnungszeiten', 'kontakt', 'adresse', 'anschrift',
  'impressum', 'agb', 'datenschutz', 'cookie', 'cookies',
  'versandkosten', 'versand', 'lieferung', 'lieferzeit', 'porto', 'fracht',
  'rückgabe', 'rueckgabe', 'widerruf', 'widerrufsrecht', 'garantie', 'gewährleistung',
  'bezahlung', 'zahlung', 'rechnung', 'barzahlung', 'kreditkarte',
  'gutschein', 'rabattcode', 'aktionscode',
  'hours', 'opening', 'address', 'contact', 'shipping', 'delivery', 'refund',
  'return', 'warranty', 'payment', 'invoice', 'policy', 'discount', 'coupon',
])

const ENGLISH_SIGNALS = /\b(the|is|are|what|when|where|how|why|can|do|does|your|you|we|our|have|has|will|would|should|could|please|hello|hi|thanks|thank|order|product|price|stock|available|shipping|delivery|refund|return|cancel|hours|opening|address|email|phone)\b/i
const GERMAN_SIGNALS = /\b(ich|du|er|sie|es|wir|ihr|und|oder|aber|für|fuer|mit|von|zu|im|dem|der|die|das|den|des|ein|eine|einer|eines|einem|einen|nicht|ist|sind|war|haben|hat|habt|kann|können|koennen|bitte|danke|hallo|guten|was|wie|wo|wann|warum|welche|welcher|welches|kostet|kosten|gibt|brauche|brauchen|suche|suchen|möchte|moechte|möchten|mir|mich|dir|dich|uns|euch|auf|lager|verfügbar|verfuegbar)\b/i
const GERMAN_CHARS = /[äöüßÄÖÜ]/
const AMBIGUOUS_GERMAN_SIGNALS = new Set([
  'war', 'was', 'hat', 'von', 'mit', 'das', 'der', 'die', 'er', 'es',
])

// Words that must NOT be used as product-title search terms. Without this,
// a follow-up like "was kostet Critical Mass? ist das auf Lager?" turns
// "das/auf/ist/lager/kostet" into title-match terms that flood the result
// limit and push the real product ("Critical Mass") out of the results.
const SEARCH_STOPWORDS = new Set<string>([
  ...PRODUCT_STOPWORDS,
  ...NON_PRODUCT_KEYWORDS,
  // German function / intent words
  'ist', 'auf', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem', 'eines',
  'mit', 'für', 'fuer', 'von', 'vom', 'zum', 'zur', 'als', 'auch', 'noch', 'nur',
  'kostet', 'kosten', 'preis', 'preise', 'viel', 'viele', 'gibt', 'haben', 'hast', 'hat',
  'welche', 'welcher', 'welches', 'brauche', 'brauchen', 'suche', 'suchen', 'will',
  'möchte', 'moechte', 'möchten', 'mir', 'mich', 'dir', 'dich', 'uns', 'euch',
  'sie', 'wir', 'denn', 'bitte', 'danke', 'hallo', 'hey', 'lager', 'verfügbar',
  'verfuegbar', 'online', 'shop', 'eures', 'euer', 'eure',
  'war', 'mal', 'pro', 'bin', 'gib', 'bzw',
  // English function / intent words
  'what', 'which', 'whats', 'how', 'much', 'cost', 'costs', 'price', 'priced',
  'stock', 'available', 'have', 'has', 'want', 'need', 'looking', 'your', 'our',
  'this', 'that', 'those', 'these', 'with', 'for', 'and', 'the', 'does', 'can',
  'could', 'would', 'should', 'please', 'thanks', 'hello', 'hey', 'get', 'got',
])

/**
 * Extract meaningful product-search terms from a message: length > 2, accents
 * stripped, and stopwords/intent-words removed so only real product-name-ish
 * tokens remain. Lowercased (Payload `contains`/`like` are case-insensitive).
 */
export function extractSearchKeywords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[\s\-–—/]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]+/gu, ''))
    .filter((w) => w.length > 2 && !SEARCH_STOPWORDS.has(w))
}

const MUSHROOM_SYNONYM_RE =
  /^(kubanisch\w*|cubensis|psilocyb\w*|zauberpilz\w*|pilzzucht\w*|pilzset\w*|zuchtset\w*|magicmushroom\w*)$/

/**
 * Cross-language EN→DE product search synonyms and botanical/latin mappings.
 * Expands English query keywords/phrases into German catalogue terms and category names.
 */
export const EN_DE_SYNONYMS: Record<string, string[]> = {
  // cactus / Kakteen / Kaktus
  cactus: ['kaktus', 'kakteen'],
  cacti: ['kaktus', 'kakteen'],
  cactuses: ['kaktus', 'kakteen'],

  // cutting / Steckling
  cutting: ['steckling', 'stecklinge'],
  cuttings: ['steckling', 'stecklinge'],

  // seedling / Sämling
  seedling: ['sämling', 'saemling', 'sämlinge', 'saemlinge'],
  seedlings: ['sämling', 'saemling', 'sämlinge', 'saemlinge'],

  // mushroom kit / Growkit / Pilzzucht (also kit / Set)
  'mushroom kit': ['growkit', 'pilzzucht', 'pilzzuchtbedarf', 'set'],
  'mushroom kits': ['growkit', 'pilzzucht', 'pilzzuchtbedarf', 'set'],
  'grow kit': ['growkit', 'pilzzucht', 'set'],
  'grow kits': ['growkit', 'pilzzucht', 'set'],
  kit: ['set', 'growkit'],
  kits: ['set', 'growkit'],

  // seeds / Samen
  seeds: ['samen', 'saatgut', 'hanfsamen'],
  seed: ['samen', 'saatgut', 'hanfsamen'],

  // soil / Erde
  soil: ['erde', 'erden', 'substrat', 'substrate'],

  // nutrients / Dünger
  nutrients: ['dünger', 'duenger'],
  nutrient: ['dünger', 'duenger'],
  fertilizer: ['dünger', 'duenger'],
  fertilizers: ['dünger', 'duenger'],

  // light / Beleuchtung
  light: ['beleuchtung', 'lampe', 'lampen', 'leuchtmittel'],
  lights: ['beleuchtung', 'lampe', 'lampen', 'leuchtmittel'],

  // fan / Lüfter
  fan: ['lüfter', 'luefter', 'ventilator', 'ventilatoren', 'belüftung', 'belueftung'],
  fans: ['lüfter', 'luefter', 'ventilator', 'ventilatoren', 'belüftung', 'belueftung'],

  // Botanical / common names (e.g. San Pedro -> Trichocereus Pachanoi)
  'san pedro': ['trichocereus', 'pachanoi', 'kaktus', 'kakteen'],
  'san pedro cactus': ['trichocereus', 'pachanoi', 'kaktus', 'kakteen'],
  peyote: ['lophophora', 'williamsii', 'kaktus', 'kakteen'],
}

/**
 * Return translated EN->DE synonyms for a single word or phrase.
 */
export function expandSynonyms(term: string): string[] {
  const lower = term.toLowerCase().trim()
  return EN_DE_SYNONYMS[lower] ? [...EN_DE_SYNONYMS[lower]] : []
}

/**
 * Expand a list of keywords with EN->DE synonyms and check the full query string
 * for multi-word phrases (e.g. "mushroom kit", "san pedro cactus").
 */
export function expandKeywordsWithSynonyms(keywords: string[], fullText = ''): string[] {
  const result = new Set<string>(keywords)
  const lowerText = fullText.toLowerCase()

  // 1. Multi-word phrase matches against the full text (e.g. "mushroom kit", "san pedro")
  for (const [phrase, syns] of Object.entries(EN_DE_SYNONYMS)) {
    if (phrase.includes(' ') && lowerText.includes(phrase)) {
      for (const syn of syns) {
        result.add(syn)
      }
    }
  }

  // 2. Single-keyword synonym lookups
  for (const kw of keywords) {
    const syns = EN_DE_SYNONYMS[kw.toLowerCase()]
    if (syns) {
      for (const syn of syns) {
        result.add(syn)
      }
    }
  }

  return [...result]
}

const ALLOWED_SHORT_KEYWORDS = new Set(['san', 'fan', 'kit', 'set', 'cbd'])

/**
 * Morphology-lite variants of a keyword so inflected forms still match product
 * titles by substring. German plurals/cases add endings ("Messbechern" vs the
 * product "Messbecher"), which exact substring matching misses. We add the word
 * with common endings stripped, plus a short prefix stem for longer words,
 * and cross-language synonyms.
 * Kept ≥4 chars to prevent over-matching, while allowing essential short keywords ("san", "fan", "kit", "set", "cbd").
 */
export function keywordVariants(kw: string): string[] {
  const lower = kw.toLowerCase()
  const out = new Set<string>([lower])
  for (const suf of ['en', 'ern', 'er', 'nen', 'n', 'e', 's']) {
    if (lower.endsWith(suf) && lower.length - suf.length >= 4) {
      out.add(lower.slice(0, -suf.length))
    }
  }
  // Prefix stem for longer words — catches remaining inflections and compounds.
  if (lower.length >= 6) out.add(lower.slice(0, lower.length - 2))
  // Mushroom grow kits are titled "Mushroom Growkit …" — map species/colloquial
  // terms (kubanisch, cubensis, Psilocybe, Zauberpilz…) onto those title words.
  if (MUSHROOM_SYNONYM_RE.test(lower)) {
    out.add('growkit')
    out.add('mushroom')
    out.add('psilocybe')
  }

  // Cross-language EN->DE synonyms (e.g. cactus -> kaktus, kakteen; fan -> lüfter)
  const syns = EN_DE_SYNONYMS[lower]
  if (syns) {
    for (const s of syns) {
      out.add(s)
      for (const suf of ['en', 'ern', 'er', 'nen', 'n', 'e', 's']) {
        if (s.endsWith(suf) && s.length - suf.length >= 4) {
          out.add(s.slice(0, -suf.length))
        }
      }
      if (s.length >= 6) out.add(s.slice(0, s.length - 2))
    }
  }

  return [...out].filter((w) => w.length >= 4 || ALLOWED_SHORT_KEYWORDS.has(w))
}

export function hasProductKeyword(text: string): boolean {
  const words = text.toLowerCase().split(/[\s\-–—/]+/)
  return words.some((raw) => {
    const w = raw.replace(/[^\p{L}\p{N}]+/gu, '')
    if (w.length <= 2) return false
    if (PRODUCT_STOPWORDS.has(w)) return false
    if (NON_PRODUCT_KEYWORDS.has(w)) return false
    return true
  })
}

export function formatPrice(p: any): string {
  if (p.enableVariants && Array.isArray(p.variants)) {
    const prices: number[] = []
    for (const v of p.variants) {
      if (Array.isArray(v?.values)) {
        for (const val of v.values) {
          if (typeof val?.price === 'number' && val.price > 0) {
            prices.push(val.price)
          }
        }
      }
    }
    if (prices.length > 0) {
      const min = Math.min(...prices)
      const max = Math.max(...prices)
      return min === max ? `€${min}` : `€${min}–€${max}`
    }
  }
  // Prefer priceInEUR when it's a positive number. priceInEUR is the
  // "official" cart-subtotal price (see src/collections/Products
  // index.ts — `priceInEUR` is what the ecommerce plugin reads for
  // cart math; the beforeChange hook defaults it from basePrice).
  if (typeof p.priceInEUR === 'number' && p.priceInEUR > 0) {
    return `€${p.priceInEUR}`
  }
  // FALL BACK to basePrice. This is the same policy the storefront's
  // search endpoint uses (src/lib/search/pgSearch.ts):
  //   coalesce(nullif(price_in_e_u_r, 0), base_price) AS price
  // i.e. priceInEUR = 0 means "fall back to basePrice" — NOT "no
  // price configured". Keeping the chat route aligned with the
  // search endpoint matters because the customer sees the search
  // result page say €12,90 and the bot say "Preis auf Anfrage"
  // for the same product, and the latter loses the sale.
  //
  // Earlier this function had `priceInEUR == null` (only fall back
  // when null/undefined, NOT when 0). That was tightened to fix a
  // different bug where a stale basePrice (29.9) leaked through
  // for a product whose priceInEUR had been deliberately cleared.
  // That fix overshot — many products in the catalogue have
  // priceInEUR = 0 simply because the field was never touched (the
  // hook defaults it from basePrice, but only on the NEXT write),
  // and those products now show "Preis auf Anfrage" instead of
  // their real price. Reverting to the search-endpoint behaviour
  // keeps the customer and the bot on the same page. If a
  // product genuinely needs "Preis auf Anfrage", the cleanest
  // path is to set basePrice to 0 / null in the admin (the same
  // effect at every display point), not to rely on priceInEUR=0
  // as an "unset" marker that other parts of the system don't
  // respect anyway.
  if (typeof p.basePrice === 'number' && p.basePrice > 0) {
    return `€${p.basePrice}`
  }
  return ''
}

function isLanguageNeutral(text: string): boolean {
  const rawTokens = text.trim().split(/\s+/).filter(Boolean)
  if (rawTokens.length === 0 || rawTokens.length > 3) return false
  if (GERMAN_CHARS.test(text)) return false
  const cleanTokens = rawTokens
    .map((t) => t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ''))
    .filter(Boolean)
  if (cleanTokens.length === 0) return true
  return cleanTokens.every(
    (t) => !SEARCH_STOPWORDS.has(t) && !ENGLISH_SIGNALS.test(t) && !GERMAN_SIGNALS.test(t)
  )
}

function extractMatchingSignals(text: string, regex: RegExp): Set<string> {
  const matches = new Set<string>()
  const r = new RegExp(regex.source, 'gi')
  let m: RegExpExecArray | null
  while ((m = r.exec(text)) !== null) {
    matches.add(m[0].toLowerCase())
  }
  return matches
}

function detectClearLanguage(text: string): 'de' | 'en' | null {
  const hasUmlauts = GERMAN_CHARS.test(text)
  const deMatches = extractMatchingSignals(text, GERMAN_SIGNALS)
  const enMatches = extractMatchingSignals(text, ENGLISH_SIGNALS)

  const strongDeMatches = new Set(
    [...deMatches].filter((w) => !AMBIGUOUS_GERMAN_SIGNALS.has(w))
  )

  // German requires at least 2 distinct signal hits (with at least 1 non-ambiguous),
  // OR a signal plus umlauts. Ambiguous tokens ('von', 'mit', etc.) cannot count on their own.
  const isDe =
    (hasUmlauts && deMatches.size >= 1) ||
    (deMatches.size >= 2 && strongDeMatches.size >= 1)

  // English requires at least 2 distinct signal hits
  const isEn = enMatches.size >= 2

  if (isDe && !isEn) return 'de'
  if (isEn && !isDe) return 'en'
  return null
}

export function resolveLanguage(
  messages: string[] | string,
  requestedLocale?: string
): 'de' | 'en' {
  const userMessages = Array.isArray(messages) ? messages : [messages]

  // Session lock: once any user message has >= 8 tokens in a clearly-detectable
  // language, return that language for the rest of the session. Scan in order,
  // first >= 8-token clear-language message wins.
  for (const msg of userMessages) {
    if (typeof msg !== 'string') continue
    const tokens = msg.trim().split(/\s+/).filter(Boolean)
    if (tokens.length >= 8) {
      const clearLang = detectClearLanguage(msg)
      if (clearLang) {
        return clearLang
      }
    }
  }

  // Detect language from the LAST user message first
  const lastMsg = userMessages.length > 0 ? userMessages[userMessages.length - 1] : ''
  if (!lastMsg || isLanguageNeutral(lastMsg)) {
    if (requestedLocale === 'en' || requestedLocale === 'de') {
      return requestedLocale
    }
    return !GERMAN_CHARS.test(lastMsg) && ENGLISH_SIGNALS.test(lastMsg) ? 'en' : 'de'
  }

  if (GERMAN_CHARS.test(lastMsg)) {
    return 'de'
  }
  const isEn = ENGLISH_SIGNALS.test(lastMsg)
  const isDe = GERMAN_SIGNALS.test(lastMsg)
  if (isEn && !isDe) {
    return 'en'
  }
  if (isDe && !isEn) {
    return 'de'
  }
  const deMatches = extractMatchingSignals(lastMsg, GERMAN_SIGNALS)
  const strongDe = [...deMatches].some((w) => !AMBIGUOUS_GERMAN_SIGNALS.has(w))
  if (isEn && !strongDe) {
    return 'en'
  }
  return !GERMAN_CHARS.test(lastMsg) && ENGLISH_SIGNALS.test(lastMsg) ? 'en' : 'de'
}

/**
 * Flatten a Lexical rich-text value (the shape stored in Payload richText
 * fields, e.g. Products.description) into a single plain-text string.
 * Walks the node tree collecting every `text` leaf. Used to feed product
 * descriptions into the chatbot RAG context so it can actually compare
 * products, not just list their names.
 */
export function lexicalToPlainText(value: any): string {
  if (!value) return ''
  const out: string[] = []
  const walk = (node: any): void => {
    if (!node || typeof node !== 'object') return
    if (typeof node.text === 'string' && node.text) out.push(node.text)
    const children = node.children
    if (Array.isArray(children)) children.forEach(walk)
  }
  walk(value.root ?? value)
  return out.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * True when the customer's message asks for an overview or comparison
 * ("what's the difference…", "which kits do you have", "compare…"), signalling
 * that the RAG should surface the whole category rather than a few keyword hits.
 */
export function wantsProductOverview(text: string): boolean {
  return /vergleich|unterschied|differ|compare|versus|\bvs\b|welche|which|\balle\b|\ball\b|liste|\blist\b|sortiment|angebot|auswahl|optionen|options|habt ihr|do you have|gibt es|verschiedene|different|übersicht|overview|empfehl|recommend/i.test(
    text
  )
}

/** Single source of truth for opening hours (chat prompt, store info). */
export const STORE_OPENING_HOURS = {
  de: 'Montag–Samstag, 11:00–19:00 Uhr',
  en: 'Monday–Saturday, 11:00–19:00 (11am–7pm)',
} as const

/**
 * Safe first name for greetings. Returns '' when the account name is missing,
 * an email address, the shop's own placeholder, or otherwise not a real name.
 */
export function extractCustomerFirstName(name?: unknown, email?: unknown): string {
  if (typeof name !== 'string') return ''
  const full = name.trim()
  if (!full || full.includes('@')) return ''
  if (full.toLowerCase() === String(email || '').trim().toLowerCase()) return ''
  const first = full.split(/\s+/)[0].slice(0, 60).replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
  if (!first || first.includes('@')) return ''
  if (/^(office|admin|info|support|stamm-?baum|shop|team|kunde|customer|guest|user)$/i.test(first)) return ''
  if (!/\p{L}.*\p{L}/u.test(first)) return ''
  return first
}
